import React from 'react';
import { observer } from 'mobx-react-lite';
import { Box, Button, ButtonGroup, Dialog, DialogActions, DialogContent, Typography } from '@mui/material';
import { Edit, LockOpen } from '@mui/icons-material';
import { ChapterIndicatorPackageDto, IndicatorDto } from '../../../../types/indicator';
import IndicatorTableRow from '../../shvk/IndicatorTableRow';
import FroalaIndicatorNote from '../../editor/FroalaIndicatorNote';
import IndicatorPackageThemeItemTable from '../../shvk/IndicatorPackageThemeItemTable';
import useStore from '../../../../store/storeContext';
import { convertEpochToDateTimeWithoutSecondsString } from '../../../../utils/dateUtils';

interface Props {
    indicator?: IndicatorDto | null;
    indicatorPackage?: ChapterIndicatorPackageDto | null;
    isOpen: boolean;
    close(): void;
    toggleEdit?(): Promise<void>;
}

function IndicatorWithNoteDialog(props: Props) {
    const { localization, snackbar, theming } = useStore();
    const { indicatorPackage, toggleEdit, close, isOpen, indicator } = props;

    const handleEdit = async (): Promise<void> => {
        if (!toggleEdit) return;
        try {
            await toggleEdit();
        } catch (error) {
            if (error.data?.['UserMessage']) {
                snackbar.setMessage(error.data?.['UserMessage']);
                snackbar.setSeverity('error');
                snackbar.open();
            } else {
                snackbar.showError(error?.data?.['TranslationKey']);
            }
        }
    };

    const handleClose = async (): Promise<void> => {
        if (!toggleEdit) return;
        if (indicatorPackage?.locked) {
            try {
                await toggleEdit();
            } catch (error) {
                if (error.data?.['UserMessage']) {
                    snackbar.setMessage(error.data?.['UserMessage']);
                    snackbar.setSeverity('error');
                    snackbar.open();
                } else {
                    snackbar.showError(error?.data?.['TranslationKey']);
                }
            }
        }
        close();
    };

    const { translate } = localization;

    return (
        <Dialog open={isOpen} onClose={close} maxWidth={'lg'} fullWidth>
            <DialogContent dividers>
                {indicator && (
                    <IndicatorPackageThemeItemTable>
                        <IndicatorTableRow
                            indicator={indicator}
                            editing={indicatorPackage?.locked}
                            toggleEdit={toggleEdit}
                        />
                    </IndicatorPackageThemeItemTable>
                )}
                <Box display="flex">
                    <Box flexGrow={1}>
                        <Typography variant="h6">
                            {translate('SUMMARY')} - {indicatorPackage?.name}
                        </Typography>
                    </Box>
                    <ButtonGroup id="button-group" color="primary" size="small">
                        <Button
                            onClick={handleEdit}
                            style={{
                                color: !indicatorPackage?.locked
                                    ? theming.palette.primary.main
                                    : theming.palette.error.main,
                            }}
                        >
                            {!indicatorPackage?.locked ? <Edit /> : <LockOpen />}
                        </Button>
                    </ButtonGroup>
                </Box>
                <Typography variant="caption">
                    <i>
                        {`${translate('CHAPTER_LAST_UPDATED')} ${
                            indicatorPackage ? indicatorPackage.editor?.userName || '' : ''
                        } ${
                            indicatorPackage
                                ? convertEpochToDateTimeWithoutSecondsString(indicatorPackage.lastUpdated)
                                : ''
                        }`}
                    </i>
                </Typography>
                {indicatorPackage?.locked ? (
                    <FroalaIndicatorNote indicatorPackage={indicatorPackage} />
                ) : (
                    <Typography
                        className="fr-view"
                        dangerouslySetInnerHTML={{ __html: indicatorPackage?.notes || '' }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button variant="contained" onClick={handleClose}>
                    {translate('CLOSE')}
                </Button>
                <Box flexGrow={1} />
            </DialogActions>
        </Dialog>
    );
}

export default observer(IndicatorWithNoteDialog);
